import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { usePreviewBedarfSenden, useSendBedarfSenden } from '../../api/bedarfe';
import { EmailEditor } from '../email/EmailEditor';
import type { MatchingBewerber } from '../../types/kennenlernen';

interface BedarfSendenModalProps {
  isOpen: boolean;
  onClose: () => void;
  bedarfId: string;
  bewerber: MatchingBewerber;
  onSuccess?: () => void;
}

/**
 * Single-step editor modal to present a Bedarf (anonymized case) to a matching
 * Bewerber via email. The preview loads on open; the text is editable before
 * sending. Mirrors the AnfordernModal editor step.
 */
export function BedarfSendenModal({
  isOpen,
  onClose,
  bedarfId,
  bewerber,
  onSuccess,
}: BedarfSendenModalProps) {
  const previewMutation = usePreviewBedarfSenden();
  const sendMutation = useSendBedarfSenden();
  const [error, setError] = useState<string | null>(null);
  const [editedHtml, setEditedHtml] = useState('');
  const [editedSubject, setEditedSubject] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    if (!bewerber.email) {
      setError('Bewerber hat keine E-Mail-Adresse.');
      return;
    }

    setError(null);
    previewMutation
      .mutateAsync({ bedarfId, bewerberId: bewerber.id })
      .then((result) => {
        setEditedHtml(result.data.html);
        setEditedSubject(result.data.subject);
      })
      .catch((err: any) => {
        const msg = err?.response?.data?.message || err?.message || 'Fehler beim Laden der Vorschau';
        setError(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join(', ') : String(msg));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, bedarfId, bewerber.id]);

  const handleSend = async () => {
    setError(null);
    try {
      await sendMutation.mutateAsync({
        bedarfId,
        bewerberId: bewerber.id,
        data: { bodyHtml: editedHtml, subject: editedSubject },
      });
      toast.success('Bedarf wurde gesendet');
      handleClose();
      onSuccess?.();
    } catch (err: any) {
      let errorMessage = 'Fehler beim Versenden der E-Mail';
      const responseMessage = err?.response?.data?.message;
      if (Array.isArray(responseMessage)) {
        errorMessage = responseMessage.join(', ');
      } else if (typeof responseMessage === 'string') {
        errorMessage = responseMessage;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setError(null);
    setEditedHtml('');
    setEditedSubject('');
    onClose();
  };

  if (!isOpen) return null;

  const bewerberName = `${bewerber.firstName} ${bewerber.lastName}`;
  const canSend = !previewMutation.isPending && !sendMutation.isPending && Boolean(editedHtml);

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Bedarf senden
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  E-Mail an <span className="font-medium">{bewerberName}</span>
                  {bewerber.email && <> ({bewerber.email})</>} bearbeiten und senden
                </p>

                {error && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {!error && (
                  <div className="mt-4">
                    <EmailEditor
                      renderedHtml={editedHtml}
                      renderedSubject={editedSubject}
                      onHtmlChange={setEditedHtml}
                      onSubjectChange={setEditedSubject}
                      isLoading={previewMutation.isPending}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSend}
              disabled={!canSend}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {sendMutation.isPending ? 'Wird versendet...' : 'Senden'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={sendMutation.isPending}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
